import http from './http';
import { isSysApiSuccess } from './sysApiCodes';
import type {
  FrontNewsDetail,
  FrontNewsPageData,
  HomeSectionItem,
  FrontRecruitmentDetail,
  FrontProductCenterData,
  FrontProductDetail,
  FrontSuccessCaseItem,
  JobItem,
  RouteMenuItem,
  SolutionItem
} from './types/cms';

function unwrapFront<T>(raw: unknown): T {
  if (raw !== null && typeof raw === 'object' && 'code' in raw) {
    const r = raw as { code: number; msg?: string; data?: T };
    if (!isSysApiSuccess(r.code)) {
      throw new Error(r.msg || '请求失败');
    }
    return r.data as T;
  }
  return raw as T;
}

async function frontGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const raw = await http.get(url, { params });
  return unwrapFront<T>(raw);
}

async function frontPost<T>(url: string, data?: Record<string, unknown>): Promise<T> {
  const raw = await http.post(url, data);
  return unwrapFront<T>(raw);
}

export type FrontNewsPageQuery = {
  pageNum?: number;
  pageSize?: number;
};

/**
 * 获取前台菜单列表
 */
export function fetchRoutesList() {
  return frontGet<RouteMenuItem[]>('/cms/routes/list');
}

/**
 * 获取首页数据
 */
export function fetchHomepage() {
  return frontGet<unknown>('/cms/homepage');
}

/**
 * 获取解决方案列表
 */
export function fetchSolutionsList() {
  return frontGet<SolutionItem[]>('/cms/solutions/list');
}

/**
 * 获取产品中心（类型 / 分类 / 产品树）
 */
export function fetchProductCenter() {
  return frontGet<FrontProductCenterData>('/cms/product-center');
}

/**
 * 获取产品详情
 */
export function fetchFrontProductDetail(id: string) {
  return frontGet<FrontProductDetail>(`/cms/products/${id}`);
}

/**
 * 获取成功案例列表
 */
export function fetchSuccessCasesList() {
  return frontGet<FrontSuccessCaseItem[]>('/cms/success-cases/list');
}

/**
 * 获取人才招聘列表
 */
export function fetchRecruitmentsList() {
  return frontGet<JobItem[]>('/cms/recruitments/list');
}

/**
 * 获取人才招聘详情
 */
export function fetchRecruitmentDetail(id: string) {
  return frontGet<FrontRecruitmentDetail>(`/cms/recruitments/${id}`);
}

/**
 * 提交留言
 */
/** 提交访客留言（message 为表单 JSON 字符串） */
export function fetchContactUsMessages(payload: { contact: string; message: string }) {
  return frontPost<unknown>('/cms/guestbook', {
    contact: payload.contact,
    message: payload.message
  });
}

/**
 * 获取新闻分页列表
 */
export function fetchNewsPage(query: FrontNewsPageQuery = {}) {
  return frontGet<FrontNewsPageData>('/cms/news/page', {
    pageNum: query.pageNum ?? 1,
    pageSize: query.pageSize ?? 12
  });
}

/**
 * 获取新闻详情（含 prev / next 上下篇）
 */
export function fetchNewsDetail(id: string) {
  return frontGet<FrontNewsDetail>(`/cms/news/${id}`);
}

/**
 * 获取首页 Banner 列表（从 homepage 中提取）
 */
export async function fetchHomeBanners(): Promise<HomeSectionItem[]> {
  const raw = await fetchHomepage();
  if (Array.isArray(raw)) {
    return raw as HomeSectionItem[];
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const banners = obj.banners ?? obj.bannerList ?? obj.sections;
    if (Array.isArray(banners)) {
      return banners as HomeSectionItem[];
    }
  }
  return [];
}
